import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
  Paper,
  type PaperProps,
} from "@mui/material";
import { RiCloseLine, RiQuestionLine, RiFullscreenLine, RiFullscreenExitLine, RiQuestionAnswerLine, RiQuestionMark, RiQuestionAnswerFill, RiQuestionnaireFill } from "@remixicon/react";
import { useState, useRef, type FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import Draggable from "react-draggable";

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  tourId: string;
}

const HelpModal: FunctionComponent<HelpModalProps> = ({
  isOpen,
  onClose,
  tourId,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const nodeRef = useRef(null);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleClose = () => {
    onClose();
    setIsLoading(true);
    setHasError(false);
    setIsMaximized(false);
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const PaperComponent = (props: PaperProps) => {
    return (
      <Draggable
        handle="#draggable-dialog-title"
        cancel={'[class*="MuiDialogContent-root"]'}
        disabled={isMaximized}
        nodeRef={nodeRef}
      >
        <Paper {...props} ref={nodeRef} />
      </Draggable>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={isMaximized ? false : "md"}
      fullWidth={!isMaximized}
      fullScreen={isMaximized}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: isMaximized ? 0 : 3,
          boxShadow: "0 8px 32px rgba(0, 80, 218, 0.15)",
          ...(isMaximized && { 
            m: 0, 
            maxHeight: '100vh',
            width: '100vw',
            height: '100vh' 
          }),
        },
      }}
    >
      <DialogTitle 
        id="draggable-dialog-title"
        sx={{ 
          cursor: isMaximized ? 'default' : 'move',
          userSelect: 'none'
        }}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <RiQuestionnaireFill size={32} color="#0050DA" />
            <div> 
              <Typography
                variant="h5"
                component="div"
                fontWeight="700"
                sx={{
                  background: "linear-gradient(135deg, #0050DA 0%, #3B6DF1 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t('helpTour.title', 'Ayuda Interactiva')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('helpTour.subtitle', 'Guía interactiva para usar la aplicación')}
              </Typography>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Tooltip title={t('helpTour.tooltips.maximize', isMaximized ? 'Restaurar' : 'Maximizar')} arrow>
              <IconButton onClick={toggleMaximize} size="small">
                {isMaximized ? <RiFullscreenExitLine /> : <RiFullscreenLine />}
              </IconButton>
            </Tooltip>
            <Tooltip title={t('helpTour.tooltips.close', 'Cerrar')} arrow>
              <IconButton onClick={handleClose} size="small">
                <RiCloseLine />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </DialogTitle>
      <DialogContent sx={{ position: 'relative' }}>
        {isLoading && (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            height={isMaximized ? 'calc(100vh - 120px)' : '400px'}
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgcolor="rgba(255, 255, 255, 0.9)"
            zIndex={10}
          >
            <CircularProgress />
          </Box>
        )}
        {hasError ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <Typography variant="body1" color="error">
              {t('helpTour.error', 'Error al cargar el tour. Inténtalo de nuevo más tarde.')}
            </Typography>
          </Box>
        ) : (
            <div style={{ 
              position: 'relative', 
              paddingBottom: isMaximized ? '0' : 'calc(52.2015% + 41px)', 
              height: isMaximized ? 'calc(100vh - 120px)' : '0', 
              width: '100%' 
            }}>
                <iframe
                src={`https://demo.arcade.software/${tourId}?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true`}
                title="Actualizar perfil profesional"
                frameBorder="0"
                loading="lazy"
                //   webkitAllowFullScreen
                //   mozAllowFullScreen
                allowFullScreen
                allow="clipboard-write"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', colorScheme: 'light', opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s' }}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                ></iframe>
            </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HelpModal;